import React from 'react';
import { motion } from 'framer-motion';
import { getGrowthStage } from '../../mastery';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
interface GrowthStageIconProps {
  masteryScore: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}
export function GrowthStageIcon({
  masteryScore,
  size = 'md',
  className = ''
}: GrowthStageIconProps) {
  const stage = getGrowthStage(masteryScore);
  const {
    shouldAnimate
  } = useMotionBudget();
  const sizeMap = {
    sm: 24,
    md: 32,
    lg: 48
  };
  const s = sizeMap[size];
  // Animation variants
  const growIn = {
    hidden: {
      scale: 0,
      opacity: 0,
      y: 10
    },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        bounce: 0.5,
        duration: 0.6
      }
    }
  };
  const sway = {
    animate: {
      rotate: [-2, 2, -2],
      transition: {
        repeat: Infinity,
        duration: 4,
        ease: 'easeInOut'
      }
    }
  };
  const pulse = {
    animate: {
      scale: [1, 1.05, 1],
      opacity: [0.8, 1, 0.8],
      transition: {
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }
    }
  };
  const renderIcon = () => {
    switch (stage.level) {
      case 0:
        // Nieuw (Seed)
        return <svg width={s} height={s} viewBox="0 0 32 32" fill="none" data-id="element-1953">
            <motion.circle cx="16" cy="24" r="4" fill="#94A3B8" variants={growIn} initial="hidden" animate="visible" data-id="element-1954" />
            <path d="M8 28h16" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" data-id="element-1955" />
          </svg>;
      case 1:
        // Net begonnen (Sprout)
        return <svg width={s} height={s} viewBox="0 0 32 32" fill="none" data-id="element-1956">
            <motion.g variants={growIn} initial="hidden" animate="visible" data-id="element-1957">
              <path d="M16 28v-8" stroke="#10B981" strokeWidth="3" strokeLinecap="round" data-id="element-1958" />
              <motion.path d="M16 22c-3-2-6-1-6-1s1 3 6 4" fill="#34D399" variants={shouldAnimate ? sway : {}} animate="animate" style={{
              transformOrigin: '16px 22px'
            }} data-id="element-1959" />
              <motion.path d="M16 20c3-2 6-1 6-1s-1 3-6 4" fill="#10B981" variants={shouldAnimate ? sway : {}} animate="animate" style={{
              transformOrigin: '16px 20px'
            }} data-id="element-1960" />
            </motion.g>
            <path d="M8 28h16" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" data-id="element-1961" />
          </svg>;
      case 2:
        // Groeit (Small Plant)
        return <svg width={s} height={s} viewBox="0 0 32 32" fill="none" data-id="element-1962">
            <motion.g variants={growIn} initial="hidden" animate="visible" data-id="element-1963">
              <path d="M16 28V12" stroke="#10B981" strokeWidth="3" strokeLinecap="round" data-id="element-1964" />
              <motion.path d="M16 20c-4-3-8-1-8-1s1 4 8 5" fill="#34D399" variants={shouldAnimate ? sway : {}} animate="animate" style={{
              transformOrigin: '16px 20px'
            }} data-id="element-1965" />
              <motion.path d="M16 16c4-3 8-1 8-1s-1 4-8 5" fill="#10B981" variants={shouldAnimate ? sway : {}} animate="animate" style={{
              transformOrigin: '16px 16px',
              animationDelay: '0.5s'
            }} data-id="element-1966" />
              <motion.path d="M16 12c-3-3-6-1-6-1s1 3 6 4" fill="#059669" variants={shouldAnimate ? sway : {}} animate="animate" style={{
              transformOrigin: '16px 12px',
              animationDelay: '1s'
            }} data-id="element-1967" />
            </motion.g>
            <path d="M8 28h16" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" data-id="element-1968" />
          </svg>;
      case 3:
        // Goed op weg (Flower)
        return <svg width={s} height={s} viewBox="0 0 32 32" fill="none" data-id="element-1969">
            <motion.g variants={growIn} initial="hidden" animate="visible" data-id="element-1970">
              <path d="M16 28V14" stroke="#10B981" strokeWidth="3" strokeLinecap="round" data-id="element-1971" />
              <path d="M16 22c-4-3-8-1-8-1s1 4 8 5" fill="#34D399" data-id="element-1972" />
              <path d="M16 18c4-3 8-1 8-1s-1 4-8 5" fill="#10B981" data-id="element-1973" />

              {/* Flower petals */}
              <motion.g variants={shouldAnimate ? sway : {}} animate="animate" style={{
              transformOrigin: '16px 10px'
            }} data-id="element-1974">
                <circle cx="16" cy="6" r="4" fill="#F472B6" data-id="element-1975" />
                <circle cx="20" cy="10" r="4" fill="#EC4899" data-id="element-1976" />
                <circle cx="16" cy="14" r="4" fill="#DB2777" data-id="element-1977" />
                <circle cx="12" cy="10" r="4" fill="#EC4899" data-id="element-1978" />
                <circle cx="16" cy="10" r="3" fill="#FBBF24" data-id="element-1979" />
              </motion.g>
            </motion.g>
            <path d="M8 28h16" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" data-id="element-1980" />
          </svg>;
      case 4:
        // Sterk (Tree)
        return <svg width={s} height={s} viewBox="0 0 32 32" fill="none" data-id="element-1981">
            <motion.g variants={growIn} initial="hidden" animate="visible" data-id="element-1982">
              {/* Trunk */}
              <path d="M14 28v-12c0-2 4-2 4 0v12" fill="#92400E" data-id="element-1983" />
              <path d="M16 28V16" stroke="#78350F" strokeWidth="2" data-id="element-1984" />

              {/* Canopy */}
              <motion.g variants={shouldAnimate ? sway : {}} animate="animate" style={{
              transformOrigin: '16px 28px'
            }} data-id="element-1985">
                <circle cx="16" cy="12" r="10" fill="#10B981" data-id="element-1986" />
                <circle cx="10" cy="14" r="6" fill="#059669" data-id="element-1987" />
                <circle cx="22" cy="14" r="6" fill="#34D399" data-id="element-1988" />
                <circle cx="16" cy="6" r="6" fill="#34D399" data-id="element-1989" />
                {/* Apples/Fruits */}
                <circle cx="12" cy="10" r="1.5" fill="#EF4444" data-id="element-1990" />
                <circle cx="20" cy="12" r="1.5" fill="#EF4444" data-id="element-1991" />
                <circle cx="16" cy="16" r="1.5" fill="#EF4444" data-id="element-1992" />
              </motion.g>
            </motion.g>
            <path d="M6 28h20" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" data-id="element-1993" />
          </svg>;
      case 5:
        // Meester! (Golden Tree)
        return <svg width={s} height={s} viewBox="0 0 32 32" fill="none" data-id="element-1994">
            <motion.g variants={growIn} initial="hidden" animate="visible" data-id="element-1995">
              {/* Glow */}
              <motion.circle cx="16" cy="12" r="14" fill="#FEF3C7" variants={shouldAnimate ? pulse : {}} animate="animate" data-id="element-1996" />

              {/* Trunk */}
              <path d="M14 28v-12c0-2 4-2 4 0v12" fill="#B45309" data-id="element-1997" />
              <path d="M16 28V16" stroke="#92400E" strokeWidth="2" data-id="element-1998" />

              {/* Canopy */}
              <motion.g variants={shouldAnimate ? sway : {}} animate="animate" style={{
              transformOrigin: '16px 28px'
            }} data-id="element-1999">
                <circle cx="16" cy="12" r="10" fill="#F59E0B" data-id="element-2000" />
                <circle cx="10" cy="14" r="6" fill="#D97706" data-id="element-2001" />
                <circle cx="22" cy="14" r="6" fill="#FBBF24" data-id="element-2002" />
                <circle cx="16" cy="6" r="6" fill="#FCD34D" data-id="element-2003" />

                {/* Sparkles */}
                <path d="M12 6l1-2 1 2 2 1-2 1-1 2-1-2-2-1 2-1z" fill="#FFFBEB" data-id="element-2004" />
                <path d="M22 8l.5-1 .5 1 1 .5-1 .5-.5 1-.5-1-1-.5 1-.5z" fill="#FFFBEB" data-id="element-2005" />
                <path d="M10 18l.5-1 .5 1 1 .5-1 .5-.5 1-.5-1-1-.5 1-.5z" fill="#FFFBEB" data-id="element-2006" />
              </motion.g>
            </motion.g>
            <path d="M6 28h20" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" data-id="element-2007" />
          </svg>;
      default:
        return null;
    }
  };
  return <div className={`inline-flex items-center justify-center ${className}`} title={stage.label} data-id="element-2008">
      {renderIcon()}
    </div>;
}